import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MessageFormatter.css';

/**
 * MessageFormatter component to render markdown content like Claude Code terminal
 * @param {Object} props
 * @param {string} props.content - The markdown content to render
 * @param {boolean} props.isStreaming - Whether the message is still streaming
 */
const MessageFormatter = ({ content, isStreaming = false }) => {
  if (!content) return null;

  // Custom components for markdown rendering
  const components = {
    // Code blocks with syntax highlighting
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      // Map special language types to colors
      const getBlockStyle = (lang) => {
        switch(lang) {
          case 'error':
            return 'code-block-error';
          case 'success':
            return 'code-block-success';
          case 'tool':
            return 'code-block-tool';
          case 'result':
            return 'code-block-result';
          case 'thinking':
            return 'code-block-thinking';
          case 'output':
            return 'code-block-output';
          default:
            return 'code-block-default';
        }
      };

      return !inline && match ? (
        <div className={`code-block ${getBlockStyle(language)}`}>
          {['error', 'success', 'tool', 'result', 'thinking', 'output'].includes(language) ? (
            // Custom rendering for special blocks
            <div className="special-block">
              <pre className="special-block-content">
                <code>{String(children).replace(/\n$/, '')}</code>
              </pre>
            </div>
          ) : (
            // Syntax highlighted code for programming languages
            <SyntaxHighlighter
              style={vscDarkPlus}
              language={language}
              PreTag="div"
              customStyle={{
                margin: 0,
                borderRadius: '4px',
                fontSize: '13px',
                lineHeight: '1.5'
              }}
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          )}
        </div>
      ) : (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    },
    // Headers
    h1: ({ children }) => <h1 className="terminal-h1">{children}</h1>,
    h2: ({ children }) => <h2 className="terminal-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="terminal-h3">{children}</h3>,
    // Lists
    ul: ({ children }) => <ul className="terminal-list">{children}</ul>,
    ol: ({ children }) => <ol className="terminal-list-ordered">{children}</ol>,
    li: ({ children }) => <li className="terminal-list-item">{children}</li>,
    // Paragraphs
    p: ({ children }) => <p className="terminal-paragraph">{children}</p>,
    // Horizontal rule
    hr: () => <hr className="terminal-divider" />,
    // Emphasis
    em: ({ children }) => <em className="terminal-emphasis">{children}</em>,
    strong: ({ children }) => <strong className="terminal-strong">{children}</strong>,
    // Blockquote
    blockquote: ({ children }) => <blockquote className="terminal-blockquote">{children}</blockquote>,
  };

  return (
    <div className={`message-formatter ${isStreaming ? 'streaming' : ''}`}>
      <ReactMarkdown components={components}>
        {content}
      </ReactMarkdown>
      {isStreaming && <span className="cursor-blink">▊</span>}
    </div>
  );
};

export default MessageFormatter;